
import React from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form } from 'react-bootstrap'
import moment from 'moment';
// api
import { getMateriasIbic } from 'api/company';
import { getBookBindings } from 'api/libros';
import { getAllAuthors } from 'api/products';
// components
import Input from 'components/backoffice/customInput';
import SearchEntity from '../customInput/SearchEntity';
import DatePicker from 'components/backoffice/dateTimePicker';
import SearchLanguage from '../customInput/SearchLanguage';

const BookForm = ({ control, book }) => {
  const { t } = useTranslation(['backoffice']);
 
  return (
    <Row>
      <Col md={4} className='mt-4'>
        <Form.Label className="text-light">{t('autores')}</Form.Label>
        <Controller
          control={control}
          name="authors"
          defaultValue={Array.isArray(book?.authors)
            ? book.authors.map(item => ({
              value: item.id,
              label: item.nombre_autor,
            }))
            : []}
          render={({ field }) => {
            return <SearchEntity
              {...field}
              fetchData={getAllAuthors}
              labelField="nombre_autor"
              valueField="id"
              onSelected={(items) => field.onChange(items)}
              defaultValue={field.value}
              placeholder={t('searchByAuthors')}
              isMulti={true}
              formatCustomLabel={true}
            />
          }

          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='languages'
          defaultValue={Array.isArray(book?.idioma)
            ? book.idioma.map(item => ({
              label: item.nombre,
              value: item.id,
            }))
            : []}
          render={({ field: { onChange, value } }) =>
            <SearchLanguage
              showLabel={t('language')}
              defaultValue={value}
              onSelected={(selected) => {
                onChange(selected);
              }}
              labelField="nombre"
              valueField="id"
            />
          }
        />
      </Col>
      <Col sm={4} className='mt-3'>
        <Controller
          name="publication_date"
          defaultValue={book?.publication_date === '2021-01-01' ? t('publicationDate') : book?.publication_date}
          control={control}
          render={({ field: { onChange, value } }) => (
             <DatePicker
              label={t('publicationDate')}
              className="input-control w-100 form-control"
              value={value ? moment(value).local().toDate() : null}
              onChange={onChange}
              minDate={moment("2010-01-01").toDate()}
              maxDate={moment().toDate()}
              dateFormat="yyyy-MM-dd"
              showTimeInput={false}
              placeholderText={t('publicationDate')}
            />
          )}
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='num_paginas'
          defaultValue={book?.num_paginas}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('numPages')}
              placeholder={t('numPages')}
              type="number"
              value={value}
              onChange={(e) => onChange(parseInt(e, 10))}
            />
          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='num_edicion'
          defaultValue={book?.num_edicion}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('numEdition')}
              placeholder={t('numEdition')}
              type="number"
              value={value}
              onChange={(e) => onChange(parseInt(e, 10))}
            />
          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='alto_cm'
          defaultValue={book?.alto_cm}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('height')}
              placeholder={t('height')}
              type="number"
              value={value}
              onChange={(e) => onChange(parseInt(e, 10))}
            />
          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='ancho_cm'
          defaultValue={book?.ancho_cm}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('width')}
              placeholder={t('width')}
              type="number"
              value={value}
              onChange={(e) => onChange(parseInt(e, 10))}
            />
          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='grosor_cm'
          defaultValue={book?.grosor_cm}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('thickness')}
              placeholder={t('thickness')}
              type="number"
              value={value}
              onChange={(e) => onChange(parseInt(e, 10))}
            />
          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='peso'
          defaultValue={book?.peso}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('weight')}
              placeholder={t('weight')}
              type="number"
              value={value}
              onChange={(e) => onChange(parseInt(e, 10))}
            />
          }
        />
      </Col>
      <Col md={4} className="mt-4">
        <Form.Label className="text-light">{t('ibicSubjects')}</Form.Label>
        <Controller
          control={control}
          name="ibic"
          defaultValue={Array.isArray(book?.materia_ibic)
            ? book.materia_ibic.map(item => ({
              value: item.id,
              label: `${item.codigo} (${item.descripcion})`,
            }))
            : []}
          render={({ field }) => (
            <SearchEntity
              {...field}
              fetchData={getMateriasIbic}
              filterField="codigo,descripcion"
              labelField="codigo"
              valueField="id"
              additionalFields={['descripcion']}
              onSelected={(items) => field.onChange(items)}
              defaultValue={field.value}
              placeholder={t('ibicSubjects')}
              isMulti={true}
              formatCustomLabel={true}
            />
          )}
        />
      </Col>
      <Col md={4} className="mt-4 select">
        <Form.Label className="text-light">{t('bookBinding')}</Form.Label>
        <Controller
          control={control}
          name="bookbindings"
          defaultValue={Array.isArray(book?.encuadernacion)
            ? book.encuadernacion.map((item) => ({
              value: item.id,
              label: item.descripcion,
            }))
            : []}
          render={({ field }) => (
            <SearchEntity
              {...field}
              fetchData={getBookBindings}
              filterField="descripcion"
              labelField="descripcion"
              valueField="id"
              onSelected={(items) => field.onChange(items)}
              defaultValue={field.value}
              placeholder={t('bookBinding')}
              isMulti={true}
              formatCustomLabel={true}
            />
          )}
        />
      </Col>
    </Row>
  );
};

BookForm.propTypes = {
  control: PropTypes.any,
  book: PropTypes.any,
};

export default BookForm;